import { FormContainer, Worksheet, formatCurrency } from "uikit"
import React from "react"
import { Checkbox, Grid, Dropdown } from "semantic-ui-react"
import { useStore } from "store"
import { Footer } from "./Footer"

const AccountCheckboxGroup = ({
  studentLoanAccounts,
  accountIds,
  changableAccountsIds,
  refinanceableAccountsIds
}) => (
  <Grid columns="equal" style={{ padding: "1.5em 0 1.5em 0" }}>
    {Object.keys(studentLoanAccounts).map(accountId => (
      <Grid.Row textAlign="left" key={accountId} style={{ padding: "0px" }}>
        <Grid.Column>
          <Checkbox
            label={`${studentLoanAccounts[accountId].name} (${formatCurrency(
              -studentLoanAccounts[accountId].balance
            )})`}
            checked={
              !!accountIds.array.find(
                element => element.fields.id.value === accountId
              )
            }
            onChange={(event, eventData) => {
              if (eventData.checked) {
                accountIds.append({ id: accountId })
              } else {
                accountIds.array
                  .find(element => element.fields.id.value === accountId)
                  .remove()
                const changableAccount = changableAccountsIds.array.find(
                  element => element.fields.id.value === accountId
                )
                if (changableAccount) {
                  changableAccountsIds.array
                    .find(element => element.fields.id.value === accountId)
                    .remove()
                }
                const refinanceableAccount = refinanceableAccountsIds.array.find(
                  element => element.fields.id.value === accountId
                )
                if (refinanceableAccount) {
                  refinanceableAccountsIds.array
                    .find(element => element.fields.id.value === accountId)
                    .remove()
                }
              }
            }}
          />
        </Grid.Column>
      </Grid.Row>
    ))}
  </Grid>
)
const ChangableAccountCheckboxGroup = ({
  changableLoanAccounts,
  accountIds
}) => (
  <Grid columns="equal" style={{ padding: "1.5em 0 1.5em 0" }}>
    {Object.keys(changableLoanAccounts).map(accountId => (
      <Grid.Row textAlign="left" key={accountId} style={{ padding: "0px" }}>
        <Grid.Column>
          <Checkbox
            label={`${changableLoanAccounts[accountId].name} (${formatCurrency(
              -changableLoanAccounts[accountId].balance
            )})`}
            checked={
              !!accountIds.array.find(
                element => element.fields.id.value === accountId
              )
            }
            onChange={(event, eventData) => {
              if (eventData.checked) {
                accountIds.append({ id: accountId })
              } else {
                accountIds.array
                  .find(element => element.fields.id.value === accountId)
                  .remove()
              }
            }}
          />
        </Grid.Column>
      </Grid.Row>
    ))}
  </Grid>
)
const RefinanceableAccountCheckboxGroup = ({
  refinanceableLoanAccounts,
  accountIds
}) => (
  <Grid columns="equal" style={{ padding: "1.5em 0 1.5em 0" }}>
    {Object.keys(refinanceableLoanAccounts).map(accountId => (
      <Grid.Row textAlign="left" key={accountId} style={{ padding: "0px" }}>
        <Grid.Column>
          <Checkbox
            label={`${
              refinanceableLoanAccounts[accountId].name
            } (${formatCurrency(
              -refinanceableLoanAccounts[accountId].balance
            )})`}
            checked={
              !!accountIds.array.find(
                element => element.fields.id.value === accountId
              )
            }
            onChange={(event, eventData) => {
              if (eventData.checked) {
                accountIds.append({ id: accountId })
              } else {
                accountIds.array
                  .find(element => element.fields.id.value === accountId)
                  .remove()
              }
            }}
          />
        </Grid.Column>
      </Grid.Row>
    ))}
  </Grid>
)

const StudentLoanPayoffForm = ({
  data: {
    fields: {
      account_ids,
      account_id_error,
      changeable_ids,
      refinanceable_ids,
      person_details
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  studentLoanAccounts,
  changableLoanAccounts,
  checkChangableRepaymentPlan,
  getChangableStudentLoans,
  getRefinanceableStudentLoans,
  getPersonList,
  creditRatingOptions,
  hasSpouse,
  isNew
}) => {
  const personList = getPersonList(refinanceable_ids)
  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row
          paddedField
          label="What student loans do you want to optimize payoff for?"
          description={
            <>
              All student loans allow for penalty-free prepayment. SAVVI can
              help you determine the right prepayment plan for you given
              everything else you have going on.
            </>
          }
        >
          <Worksheet.Field padded error={account_id_error.error}>
            <AccountCheckboxGroup
              studentLoanAccounts={studentLoanAccounts}
              accountIds={account_ids}
              changableAccountsIds={changeable_ids}
              refinanceableAccountsIds={refinanceable_ids}
            />
          </Worksheet.Field>
        </Worksheet.Row>
        {checkChangableRepaymentPlan(account_ids) ? (
          <Worksheet.Row
            paddedField
            label="What student loans do you want to consider alternate payment plans for?"
            description={
              <>
                Federal Student plans, in particular, Federal Direct loans and
                some FFEL loans are eligible for changes in their repayment
                plan. SAVVI considers changing Standard/Extended repayment to
                Revised Pay as You Earn (REPAYE) repayment plan, or to
                Income-Contingent Repayment Plans (ICR) in case of Parent Plus
                Direct loans. SAVVI also considers changing REPAYE plans to
                Standard plans. Please note that Perkins loans and FFEL Parent
                Plus (as well as any Consolidation loan which includes them) are
                not eligible for Income Dependent repayment plans. Moreover
                Direct Parent Plus loans are only eligible for Standard/Extended
                or ICR plans if they are consolidated into a Direct Plan. For
                more information please consult{" "}
                <a
                  href="https://studentaid.gov/manage-loans/repayment/plans"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  studentaid.gov
                </a>
                Only check the box for federal loans that are eligible to make
                this change.
                <b>
                  Only check the box for federal loans that are eligible to make
                  this change.
                </b>
              </>
            }
          >
            <Worksheet.Field padded>
              <ChangableAccountCheckboxGroup
                changableLoanAccounts={getChangableStudentLoans(account_ids)}
                accountIds={changeable_ids}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        {account_ids.array.length > 0 ? (
          <Worksheet.Row
            paddedField
            label="What student loans do you want to consider refinancing?"
            description={
              <>
                Although you can refinance both federal and private loans, if
                you choose to refinance a federal loan you will permanently lose
                its unique benefits, such as forgiveness or income-derived
                repayment.{" "}
                <b>
                  Please consider carefully how important federal loan benefits
                  are to you before choosing to refinance a federal loan.
                </b>
              </>
            }
          >
            <Worksheet.Field padded>
              <RefinanceableAccountCheckboxGroup
                refinanceableLoanAccounts={getRefinanceableStudentLoans(
                  account_ids
                )}
                accountIds={refinanceable_ids}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        {refinanceable_ids.array.length > 0
          ? person_details.array.map(person => {
              const personName = hasSpouse
                ? person.fields.first_name.value + "'s"
                : "your"
              if (
                Object.keys(personList).includes(person.fields.person_id.value)
              ) {
                return (
                  <Worksheet.Row
                    label={`What is ${personName} credit rating?`}
                    paddedField
                    key={person.fields.person_id.value}
                  >
                    <Worksheet.Field>
                      <Dropdown
                        fluid
                        {...person.fields.credit_rating}
                        search
                        selection
                        options={creditRatingOptions}
                      />
                    </Worksheet.Field>
                  </Worksheet.Row>
                )
              } else {
                return null
              }
            })
          : null}
      </Worksheet.Body>
      <Footer
        baseUrl={baseUrl}
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        isPending={isPending}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Goal"
        error={error}
      />
    </Worksheet>
  )
}

const StudentLoanPayoffFormContainer = FormContainer(
  {
    account_ids: {
      type: "array",
      schema: {
        id: {
          type: "string"
        }
      }
    },
    account_id_error: {
      type: "string",
      required: false,
      submit: false
    },
    changeable_ids: {
      type: "array",
      schema: {
        id: {
          type: "string"
        }
      }
    },
    refinanceable_ids: {
      type: "array",
      schema: {
        id: {
          type: "string"
        }
      }
    },
    person_details: {
      type: "array",
      minLength: 0,
      required: false,
      schema: {
        person_id: {
          type: "string",
          required: false
        },
        credit_rating: {
          type: "string",
          defaultValue: "good"
        },
        first_name: {
          type: "string",
          required: false
        }
      }
    },
    type: {
      type: "string",
      defaultValue: "studentloanpayoff"
    }
  },
  ({ fields: { account_ids, account_id_error } }, ...form) => {
    if (account_ids.array.length === 0) {
      return {
        fields: {
          account_id_error: `You should select at least one account.`
        }
      }
    }
  }
)(StudentLoanPayoffForm)

const StudentLoanPayoff = ({ household, goal, scenario, ...formProps }) => {
  const [peopleObject, { updatePerson }] = useStore(
    "householdPeople",
    household.id
  )

  const people = Object.values(peopleObject)
  const hasSpouse = people.some(({ role }) => role === "spouse")
  const [accounts, { addGoal, deleteGoal, updateGoal }] = useStore(
    "householdAccounts",
    household.id
  )

  const isNew = goal === null
  const studentLoanAccounts = Object.values(accounts)
    .filter(
      ({ type }) => type === "studentloan" || type === "privatestudentloan"
    )
    .reduce((obj, account) => {
      obj[account.id] = account
      return obj
    }, {})
  const changableRepaymentPlans = ["standard", "repaye"]
  const changableLoanAccounts = Object.values(accounts)
    .filter(
      ({ type, is_perkins, is_ffel, is_parent_plus, repayment_plan }) =>
        type === "studentloan" &&
        !is_perkins &&
        !(is_ffel && is_parent_plus) &&
        changableRepaymentPlans.includes(repayment_plan)
    )
    .reduce((obj, account) => {
      obj[account.id] = account
      return obj
    }, {})
  let values = {}
  const allPersonIds = Object.values(studentLoanAccounts).map(
    account => account.person_id
  )
  const personIds = allPersonIds.filter(
    (value, index, self) => self.indexOf(value) === index
  )
  const personList = people
    .filter(({ id }) => personIds.includes(id))
    .map(person => ({
      person_id: person.id,
      first_name: person.first_name,
      credit_rating: person.credit_rating
    }))

  if (!isNew) {
    // const result = await household.getGoal(goalId)
    const studentLoanAccountsIds = goal.account_ids.filter(accountId => {
      return studentLoanAccounts[accountId]
    })
    const changableAccountsIds = goal.changeable_ids.filter(accountId => {
      return changableLoanAccounts[accountId]
    })
    const refinanceableAccountsIds = goal.refinanceable_ids.filter(
      accountId => {
        return studentLoanAccounts[accountId]
      }
    )

    values = Object.assign(
      {},
      {
        account_ids: studentLoanAccountsIds.map(accountId => {
          return { id: accountId }
        }),
        changeable_ids: changableAccountsIds.map(accountId => {
          return { id: accountId }
        }),
        refinanceable_ids: refinanceableAccountsIds.map(accountId => {
          return { id: accountId }
        }),
        person_details: personList
      }
    )
  } else {
    values = Object.assign(
      {},
      {
        person_details: personList
      }
    )
  }
  const submit = isNew
    ? async values => {
        const refinanceAccountIds = values.refinanceable_ids.map(account => {
          return account.id
        })
        const hasReferenceIds = values.refinanceable_ids.length > 0
        const personIds = Object.values(studentLoanAccounts)
          .filter(({ id }) => refinanceAccountIds.includes(id))
          .map(account => account.person_id)
        const personDetails = values.person_details.filter(person =>
          personIds.includes(person.person_id)
        )
        delete values.person_details
        Object.assign(values, {
          account_ids: values.account_ids.map(account => {
            return account.id
          }),
          changeable_ids: values.changeable_ids.map(account => {
            return account.id
          }),
          refinanceable_ids: values.refinanceable_ids.map(account => {
            return account.id
          })
        })
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...values
        })
        if (hasReferenceIds) {
          Promise.all(
            personDetails.map(({ person_id, credit_rating }) =>
              updatePerson({ id: person_id, credit_rating })
            )
          )
        }
      }
    : async values => {
        const refinanceAccountIds = values.refinanceable_ids.map(account => {
          return account.id
        })
        const hasReferenceIds = values.refinanceable_ids.length > 0
        const personIds = Object.values(studentLoanAccounts)
          .filter(({ id }) => refinanceAccountIds.includes(id))
          .map(account => account.person_id)
        const personDetails = values.person_details.filter(person =>
          personIds.includes(person.person_id)
        )
        delete values.person_details
        Object.assign(values, {
          account_ids: values.account_ids.map(account => {
            return account.id
          }),
          changeable_ids: values.changeable_ids.map(account => {
            return account.id
          }),
          refinanceable_ids: values.refinanceable_ids.map(account => {
            return account.id
          })
        })
        await updateGoal({ id: goal.id, ...values })
        if (hasReferenceIds) {
          Promise.all(
            personDetails.map(({ person_id, credit_rating }) =>
              updatePerson({ id: person_id, credit_rating })
            )
          )
        }
      }

  const onDelete = async () => {
    await deleteGoal({ scenario_id: scenario.id, id: goal.id })
    return true
  }
  const getChangableStudentLoans = accountIds => {
    const account_ids = accountIds.array.map(item => item.fields.id.value)
    const changableAccounts = Object.values(studentLoanAccounts)
      .filter(
        ({ id, is_perkins, is_ffel, is_parent_plus, repayment_plan }) =>
          account_ids.includes(id) &&
          !is_perkins &&
          !(is_ffel && is_parent_plus) &&
          changableRepaymentPlans.includes(repayment_plan)
      )
      .reduce((obj, account) => {
        obj[account.id] = account
        return obj
      }, {})
    return changableAccounts
  }
  const getRefinanceableStudentLoans = accountIds => {
    const account_ids = accountIds.array.map(item => item.fields.id.value)
    const refinanceableAccounts = Object.values(studentLoanAccounts)
      .filter(({ id }) => account_ids.includes(id))
      .reduce((obj, account) => {
        obj[account.id] = account
        return obj
      }, {})
    return refinanceableAccounts
  }
  const checkChangableRepaymentPlan = accountIds => {
    const account_ids = accountIds.array.map(item => item.fields.id.value)
    const hasChangableAccounts =
      Object.values(studentLoanAccounts).filter(
        ({ id, is_perkins, is_ffel, is_parent_plus, repayment_plan }) =>
          account_ids.includes(id) &&
          !is_perkins &&
          !(is_ffel && is_parent_plus) &&
          changableRepaymentPlans.includes(repayment_plan)
      ).length > 0
    return hasChangableAccounts
  }
  const getPersonList = accountIds => {
    const account_ids = accountIds.array.map(item => item.fields.id.value)
    const personIds = Object.values(studentLoanAccounts)
      .filter(({ id }) => account_ids.includes(id))
      .map(account => account.person_id)
    const personList = people
      .filter(({ id }) => personIds.includes(id))
      .reduce((obj, person) => {
        obj[person.id] = person
        return obj
      }, {})
    return personList
  }
  const creditRatingOptions = ["Fair", "Good", "Excellent"].map(label => ({
    text: label,
    value: label.toLowerCase()
  }))

  return (
    <StudentLoanPayoffFormContainer
      {...{
        submit,
        onDelete,
        values,
        studentLoanAccounts,
        changableLoanAccounts,
        checkChangableRepaymentPlan,
        getChangableStudentLoans,
        getRefinanceableStudentLoans,
        getPersonList,
        creditRatingOptions,
        hasSpouse,
        isNew,
        ...formProps
      }}
    />
  )
}

export default StudentLoanPayoff
